<template>
  <v-select
    v-model="activeFilters"
    :append-icon="appendIcon"
    :color="buttonColor"
    :items="availableFilters"
    :label="labelText"
    :style="style"
    dense
    hide-details
    multiple
    outlined
    class="trial-status-filters"
    @input="updateSelected($event)"
  >
    <template #selection="{ item, index }">
      <trial-status-chip v-if="displaySelectedFilters" :status="item.value" class="pointer" />
      <div v-else-if="index === 0" class="primary--text">{{ buttonText }}</div>
    </template>
    <template #item="{ item }">
      <v-simple-checkbox
        :value="isActiveFilter(item)"
        :ripple="false"
        class="mr-3"
        @input="toggleStatus(item, $event)"
      />
      <trial-status-chip :status="item.value" class="pointer" />
    </template>
  </v-select>
</template>

<script>
import _ from 'lodash'
import { TrialStatus } from '@/helpers/trial-helper'
import TrialStatusChip from '@/components/trials/TrialStatusChip'
import { permissionsMixin } from '@/mixins/permissions'
import { sessionStorageService } from '@/services/session-storage-service'

export default {
  components: { TrialStatusChip },
  props: {
    appendIcon: { type: String, default: '$dropdown' },
    displaySelectedFilters: { type: Boolean, default: false },
    defaultLabelText: { type: String, default: 'Status' }
  },
  data() {
    return {
      activeFilters: [
        TrialStatus.NOT_STARTED.value,
        TrialStatus.RECRUITING.value,
        TrialStatus.SUSPENDED.value
      ]
    }
  },
  computed: {
    includeRealTimeFeasibility() {
      return permissionsMixin.methods.hasAnyPermission(['TRIAL_REAL_TIME_FEASIBILITY_READ'])
    },
    includeMatching() {
      return permissionsMixin.methods.hasAnyPermission(['TRIAL_MATCHING_READ'])
    },
    style() {
      return this.displaySelectedFilters ? null : { 'background-color': this.backgroundColor }
    },
    hasActiveFilters() {
      return !_.isEmpty(this.activeFilters)
    },
    labelText() {
      return this.activeFilters.length === 0 ? this.defaultLabelText : null
    },
    buttonText() {
      const numberOfActiveFilters = this.activeFilters.length
      return numberOfActiveFilters > 1 ? `(${numberOfActiveFilters}) Filters` : '(1) Filter'
    },
    availableFilters() {
      const filters = _.values(TrialStatus)
      if (!this.includeRealTimeFeasibility)
        _.remove(filters, filter => filter.value === TrialStatus.FEASIBILITY.value)
      if (!this.includeMatching)
        _.remove(filters, filter => filter.value === TrialStatus.MATCHING.value)

      return filters
    },
    backgroundColor() {
      return this.hasActiveFilters ? 'rgba(39, 97, 159, 0.1)' : null
    },
    buttonColor() {
      return this.hasActiveFilters ? 'primary' : 'rgba(0, 0, 0, 0.55)'
    }
  },
  created() {
    if (this.includeMatching) {
      this.activeFilters = _.concat([TrialStatus.MATCHING.value], this.activeFilters)
    }
    if (!_.isNil(this.$route.query.trialStatusFiltersLoaded)) {
      const urlStatuses = _.concat([], _.get(this.$route.query, 'trialStatusFilters', []))
      sessionStorageService.setItem('trialStatusFiltersLoaded', true)
      sessionStorageService.setItem('trialStatusFilters', urlStatuses)
    }

    if (_.isNil(sessionStorageService.getItem('trialStatusFiltersLoaded'))) {
      this.updateSelected(this.activeFilters)
    } else {
      let values = sessionStorageService.getItem('trialStatusFilters') || []
      this.activeFilters = _.concat([], values)
      this.$emit('input', this.activeFilters)
    }
    this.$emit('loaded')
  },
  methods: {
    updateSelected(statuses) {
      sessionStorageService.setItem('trialStatusFiltersLoaded', true)
      sessionStorageService.setItem('trialStatusFilters', statuses)
      this.$emit('input', statuses)
    },
    isActiveFilter(filter) {
      return _.includes(this.activeFilters, filter.value)
    },
    toggleStatus(status, $event) {
      if ($event === true) {
        this.activeFilters = _.concat(this.activeFilters, [status.value])
      } else {
        this.activeFilters = _.filter(this.activeFilters, it => it !== status.value)
      }
      this.updateSelected(this.activeFilters)
    }
  }
}
</script>

<style lang="scss">
.trial-status-filters {
  min-width: 100px;
}
.trial-status-filters .pointer {
  cursor: pointer;
}
//https://github.com/vuetifyjs/vuetify/issues/6275 prevents the input element from taking up space in the select
.trial-status-filters .v-select__selections {
  flex-wrap: nowrap;
  width: min-content;
}
</style>
